import { useEffect, useRef, useState } from 'react';
import { isRecoveryPhrase } from '@xchange/wallet';

/** The letters of each word in what was typed or pasted: numbers, dots and spaces dropped. */
export function wordsOf(raw: string): string[] {
  return raw
    .toLowerCase()
    .split(/[^a-z]+/)
    .filter(Boolean);
}

/**
 * Recovery phrases come in 12, 15, 18, 21 or 24 words. The boxes show twelve;
 * only a longer phrase pasted whole (24 words from another wallet) adds more.
 */
function sizeFor(n: number): number {
  return Math.min(24, Math.max(12, Math.ceil(n / 3) * 3));
}

/**
 * Twelve words typed, one box each, numbered like the list they were shown in.
 * Space, enter, a dot or tab moves on; backspace in an empty box goes back.
 * Numbers and dots a person copies from the list are dropped, and a whole
 * phrase pasted into any box fills all twelve.
 *
 * With `phrase`, the words are checked against it: a box turns green when its
 * word is right, red once it is left wrong. Without it (restoring a wallet)
 * there is nothing to compare against: the boxes only collect the words, and
 * `onWords` hears them on every change. Nothing typed here leaves the device.
 */
export function WordsCheck({
  phrase,
  onMatch,
  initial = '',
  onWords,
  onDone,
  testId = 'check',
}: {
  phrase?: string;
  /** Told whether every box holds its word, each time that changes. */
  onMatch?: (all: boolean) => void;
  /** Collecting only: words to start with, when a person comes back to fix them. */
  initial?: string;
  /** Collecting only: the boxes' words, '' for an empty one, on every change. */
  onWords?: (words: string[]) => void;
  /** Enter in the last box once every box holds a word. */
  onDone?: () => void;
  /** Test ids: `<testId>-phrase` for the first box, `<testId>-word-<n>` for the rest. */
  testId?: string;
}) {
  const checking = phrase !== undefined;
  const want = checking ? wordsOf(phrase) : [];
  const [words, setWords] = useState<string[]>(() => {
    const start = checking ? [] : wordsOf(initial);
    const size = checking ? want.length : sizeFor(start.length);
    return Array.from({ length: size }, (_, i) => start[i] ?? '');
  });
  const count = words.length;
  // A box shows red only once the person has left it.
  const [left, setLeft] = useState<boolean[]>(() => Array<boolean>(count).fill(false));
  const boxes = useRef<(HTMLInputElement | null)[]>([]);

  const all = checking && words.every((w, i) => w === want[i]);
  const filled = words.every((w) => w !== '');
  useEffect(() => {
    onMatch?.(all);
  }, [all, onMatch]);
  useEffect(() => {
    onWords?.(words);
  }, [words, onWords]);

  const focus = (i: number): void => {
    const box = boxes.current[Math.max(0, Math.min(count - 1, i))];
    if (!box) return;
    box.focus();
    box.setSelectionRange(box.value.length, box.value.length);
  };

  const change = (i: number, raw: string): void => {
    const parts = wordsOf(raw);
    if (parts.length > 1) {
      // A paste: the whole phrase fills every box, a few words fill onwards.
      const whole = parts.length >= (checking ? count : 12);
      const size = whole && !checking ? sizeFor(parts.length) : count;
      const from = whole ? 0 : i;
      const next = Array.from({ length: size }, (_, k) => (whole ? '' : (words[k] ?? '')));
      const seen = Array.from({ length: size }, (_, k) => left[k] ?? false);
      parts.slice(0, size - from).forEach((w, k) => {
        next[from + k] = w;
        seen[from + k] = true;
      });
      setWords(next);
      setLeft(seen);
      if (from + parts.length >= size) boxes.current[i]?.blur();
      else focus(from + parts.length);
      return;
    }
    const word = parts[0] ?? '';
    setWords(words.map((w, k) => (k === i ? word : w)));
    // A space or a dot after a word means the next box.
    if (word !== '' && /[^a-zA-Z]$/.test(raw)) focus(i + 1);
  };

  return (
    <>
      <div className="words-check" data-testid={checking ? 'words-check' : 'words-restore'}>
        {words.map((w, i) => {
          const ok = checking && w === want[i];
          const bad = checking && !ok && (left[i] ?? false) && w !== '';
          return (
            <label key={i} className={`word-box${ok ? ' ok' : ''}${bad ? ' bad' : ''}`}>
              <span className="word-n">{i + 1}</span>
              <input
                ref={(el) => {
                  boxes.current[i] = el;
                }}
                value={w}
                onChange={(e) => {
                  change(i, e.target.value);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    if (i === count - 1 && filled) onDone?.();
                    else if (w !== '') focus(i + 1);
                  } else if (e.key === 'Backspace' && w === '' && i > 0) {
                    e.preventDefault();
                    focus(i - 1);
                  }
                }}
                onFocus={() => {
                  setLeft((l) => l.map((v, k) => (k === i ? false : v)));
                }}
                onBlur={() => {
                  setLeft((l) => l.map((v, k) => (k === i ? true : v)));
                }}
                aria-label={`Word ${i + 1}`}
                aria-invalid={bad}
                type="text"
                inputMode="text"
                enterKeyHint={i === count - 1 ? 'done' : 'next'}
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="off"
                spellCheck={false}
                data-testid={i === 0 ? `${testId}-phrase` : `${testId}-word-${i + 1}`}
              />
            </label>
          );
        })}
      </div>
      {checking && filled && !all && (
        <p className="hint" data-testid="check-mismatch">
          That doesn't match. Check your words.
        </p>
      )}
    </>
  );
}

/**
 * The words of a wallet a person already has, in the same twelve boxes.
 * Continue enables once every box holds a word; words that are not a
 * recovery phrase are told plainly after Continue, never box by box.
 */
export function RestoreWords({
  initial,
  onPhrase,
}: {
  /** Words typed earlier, when the person comes back to fix them. */
  initial: string;
  /** A valid recovery phrase, its words joined by single spaces. */
  onPhrase: (phrase: string) => void;
}) {
  const [words, setWords] = useState<string[]>([]);
  const [wrong, setWrong] = useState(false);
  const filled = words.length > 0 && words.every((w) => w !== '');
  const onWords = useCallbackOnce((w: string[]) => {
    setWords(w);
    setWrong(false);
  });
  const next = (): void => {
    if (!filled) return;
    const phrase = words.join(' ');
    if (isRecoveryPhrase(phrase)) onPhrase(phrase);
    else setWrong(true);
  };
  return (
    <>
      <WordsCheck initial={initial} onWords={onWords} onDone={next} testId="restore" />
      {wrong && (
        <p className="hint" data-testid="restore-mismatch">
          That doesn't match a recovery phrase. Check your words.
        </p>
      )}
      <button className="primary" disabled={!filled} onClick={next} data-testid="restore-done">
        Continue
      </button>
    </>
  );
}

/** A function that keeps its identity for the life of the component. */
function useCallbackOnce<A extends unknown[]>(fn: (...args: A) => void): (...args: A) => void {
  return useRef(fn).current;
}
